import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTour } from "@/context/TourContext";
import { TOURS } from "@/lib/tours";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { GraduationCap, Plus, Pencil, Trash2, PlayCircle, ExternalLink, Route, Search, Languages, Sparkles, ImagePlus, Loader2, Wand2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const MD = ({ children }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
    table: (p) => <div className="overflow-x-auto my-3"><table className="w-full text-sm border-collapse" {...p} /></div>,
    th: (p) => <th className="border border-slate-200 bg-slate-50 px-3 py-1.5 text-left font-semibold" {...p} />,
    td: (p) => <td className="border border-slate-200 px-3 py-1.5" {...p} />,
    h1: (p) => <h1 className="font-bold text-base mt-3 mb-1" {...p} />,
    h2: (p) => <h2 className="font-bold text-sm mt-3 mb-1" {...p} />,
    ul: (p) => <ul className="list-disc pl-5 space-y-1 my-2" {...p} />,
    ol: (p) => <ol className="list-decimal pl-5 space-y-1 my-2" {...p} />,
    p: (p) => <p className="my-2" {...p} />,
    strong: (p) => <strong className="font-semibold text-slate-900" {...p} />,
    code: (p) => <code className="bg-slate-100 px-1 rounded text-[13px]" {...p} />,
  }}>{children}</ReactMarkdown>
);

const EMPTY_FAQ = { question_en: "", question_es: "", answer_en: "", answer_es: "", tags: "", category: "general", module: "general", order: 999 };

const FAQ_AREAS = [
  { group: "estimating", label_en: "Estimating", modules: [
    ["paper", "Paper Printing"], ["booklet", "Booklets"], ["large-format", "Large Format"], ["stickers", "Stickers"],
    ["dtf", "DTF / Apparel"], ["embroidery", "Embroidery"], ["laser", "Laser"], ["direct-print", "Direct Print"],
    ["channel-letters", "Channel Letters"], ["sublimation", "Sublimation"], ["roll-stickers", "Roll Stickers"],
    ["catalog", "Price Catalog"], ["quotes", "My Quotes"], ["quote-builder", "Quote Builder"],
    ["store", "Store"], ["orders", "Orders"], ["training", "Training"],
  ] },
  { group: "business", label_en: "Business", modules: [
    ["products", "Products"], ["financials", "Financials"], ["profit-dashboard", "Profit & Loss"], ["machinery", "Machinery"],
    ["materials", "Materials"], ["reorder", "Reorder Center"], ["purchases", "Purchases"], ["fixed-costs", "Fixed Costs"], ["ink-estimator", "Ink Estimator"],
  ] },
  { group: "administration", label_en: "Administration", modules: [
    ["users", "Users"], ["settings", "Settings"],
  ] },
];
const FAQ_MOD_LABEL = { dashboard: "Dashboard", general: "General" };
FAQ_AREAS.forEach((a) => a.modules.forEach(([k, l]) => { FAQ_MOD_LABEL[k] = l; }));

const EMPTY_VIDEO = { title_en: "", title_es: "", description_en: "", description_es: "", url: "", category: "general", ref_id: "", ref_label: "", order: 0 };
const EMPTY_SECTION = { group: "getting_started", icon: "book", order: 999, title_en: "", title_es: "", body_en: "", body_es: "" };

export default function Training() {
  const { user } = useAuth();
  const { startTour } = useTour();
  const isAdmin = user?.role === "admin";
  const { lang } = useTour();
  const [manual, setManual] = useState([]);
  const [videos, setVideos] = useState([]);
  const [refs, setRefs] = useState({ product: [], machine: [] });
  const [videoDlg, setVideoDlg] = useState(null);   // video being added/edited
  const [sectionDlg, setSectionDlg] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [faqQuery, setFaqQuery] = useState("");
  const [faqModule, setFaqModule] = useState("all");
  const [faqDlg, setFaqDlg] = useState(null);
  const [aiBusy, setAiBusy] = useState("");

  const t = (en, es) => (lang === "en" ? en : es);
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const loadAll = async () => {
    try {
      const [m, v, fq] = await Promise.all([api.get("/training/manual"), api.get("/training/videos"), api.get("/training/faq")]);
      setManual(m.data);
      setVideos(v.data);
      setFaqs(fq.data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const [p, mac] = await Promise.all([api.get("/catalog-products"), api.get("/machines")]);
        setRefs({
          product: p.data.map((x) => ({ id: x.id, name: x.name })),
          machine: mac.data.map((x) => ({ id: x.id, name: x.name })),
        });
      } catch (e) { console.error("Failed to load training refs:", e); }
    })();
  }, [isAdmin]);

  const saveVideo = async () => {
    const body = { ...videoDlg };
    if (!body.url) { toast.error(t("Video link is required", "El enlace del video es obligatorio")); return; }
    try {
      if (body.id) await api.put(`/training/videos/${body.id}`, body);
      else await api.post("/training/videos", body);
      toast.success(t("Video saved", "Video guardado"));
      setVideoDlg(null); loadAll();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const deleteVideo = async (id) => {
    if (!window.confirm(t("Delete this video?", "¿Eliminar este video?"))) return;
    await api.delete(`/training/videos/${id}`); loadAll();
  };
  const saveSection = async () => {
    const body = { ...sectionDlg };
    try {
      if (body.id) await api.put(`/training/manual/${body.id}`, body);
      else await api.post("/training/manual", body);
      toast.success(t("Section saved", "Sección guardada"));
      setSectionDlg(null); loadAll();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const deleteSection = async (id) => {
    if (!window.confirm(t("Delete this section?", "¿Eliminar esta sección?"))) return;
    await api.delete(`/training/manual/${id}`); loadAll();
  };
  const saveFaq = async () => {
    const body = { ...faqDlg };
    if (!body.question_es && !body.question_en) { toast.error(t("Question is required", "La pregunta es obligatoria")); return; }
    if (!body.id) {
      const nq = norm(body.question_es || body.question_en);
      const dup = faqs.find((x) => nq && (norm(x.question_es) === nq || norm(x.question_en) === nq));
      if (dup) {
        if (window.confirm(t("A similar question already exists. Edit that one instead?", "Ya existe una pregunta similar. ¿Editar esa en lugar de crear una nueva?"))) {
          setFaqDlg({ ...dup });
        }
        return;
      }
    }
    try {
      if (body.id) await api.put(`/training/faq/${body.id}`, body);
      else await api.post("/training/faq", body);
      toast.success(t("Saved", "Guardado"));
      setFaqDlg(null); loadAll();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
  };
  const syncFaqDefault = async () => {
    if (!faqDlg?.id || !faqDlg?.seed_key) return;
    if (!window.confirm(t("Replace this answer with the latest system version? Your manual edits will be lost.", "¿Reemplazar esta respuesta con la última versión del sistema? Se perderán tus ediciones manuales."))) return;
    setAiBusy("sync");
    try {
      const { data } = await api.post(`/training/faq/${faqDlg.id}/sync-default`);
      setFaqDlg({ ...data });
      toast.success(t("Updated to system version", "Actualizado a la versión del sistema"));
      loadAll();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    setAiBusy("");
  };
  const deleteFaq = async (id) => {
    if (!window.confirm(t("Delete this Q&A?", "¿Eliminar esta pregunta?"))) return;
    await api.delete(`/training/faq/${id}`); loadAll();
  };
  const autoTranslate = async () => {
    const esLen = ((faqDlg.question_es || "") + (faqDlg.answer_es || "")).trim().length;
    const enLen = ((faqDlg.question_en || "") + (faqDlg.answer_en || "")).trim().length;
    const toEs = enLen >= esLen;              // richer side is the source
    const target = toEs ? "es" : "en";
    const srcQ = toEs ? faqDlg.question_en : faqDlg.question_es;
    const srcA = toEs ? faqDlg.answer_en : faqDlg.answer_es;
    if (!srcQ && !srcA) { toast.error(t("Fill one language first", "Llena un idioma primero")); return; }
    setAiBusy("translate");
    try {
      const [q, a] = await Promise.all([
        srcQ ? api.post("/training/faq/translate", { text: srcQ, target_lang: target }) : Promise.resolve({ data: { text: "" } }),
        srcA ? api.post("/training/faq/translate", { text: srcA, target_lang: target }) : Promise.resolve({ data: { text: "" } }),
      ]);
      setFaqDlg((p) => ({ ...p, ...(toEs
        ? { question_es: q.data.text || p.question_es, answer_es: a.data.text || p.answer_es }
        : { question_en: q.data.text || p.question_en, answer_en: a.data.text || p.answer_en }) }));
      toast.success(t("Translated", "Traducido"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    setAiBusy("");
  };
  const suggestKeywords = async () => {
    setAiBusy("kw");
    try {
      const { data } = await api.post("/training/faq/keywords", { question: faqDlg.question_es || faqDlg.question_en, answer: faqDlg.answer_es || faqDlg.answer_en });
      setFaqDlg((p) => ({ ...p, tags: data.tags || p.tags }));
      toast.success(t("Keywords suggested", "Palabras clave sugeridas"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    setAiBusy("");
  };
  const handleFaqImages = async (files) => {
    const list = Array.from(files || []).slice(0, 6);
    if (!list.length) return;
    setAiBusy("images");
    try {
      const b64s = await Promise.all(list.map((f) => new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(f);
      })));
      const { data } = await api.post("/training/faq/from-images", { images: b64s, lang });
      setFaqDlg((p) => ({ ...(p || EMPTY_FAQ),
        question_en: data.question_en || p?.question_en || "",
        question_es: data.question_es || p?.question_es || "",
        answer_en: data.answer_en || p?.answer_en || "",
        answer_es: data.answer_es || p?.answer_es || "",
        tags: data.tags || p?.tags || "",
      }));
      toast.success(t("Form filled from images", "Formulario llenado desde las imágenes"));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || e.message); }
    setAiBusy("");
  };

  const GROUP_LABEL = {
    getting_started: t("Getting Started", "Primeros Pasos"),
    estimating: t("Estimating", "Cotización"),
    business: t("Business", "Negocio"),
    admin: t("Administration", "Administración"),
  };
  const groups = [...new Set(manual.map((s) => s.group))];
  const fqWords = norm(faqQuery).split(/\s+/).filter(Boolean);
  const filteredFaqs = faqs.filter((x) => {
    if (faqModule !== "all" && (x.module || "general") !== faqModule) return false;
    if (fqWords.length === 0) return true;
    const hay = norm([x.question_en, x.question_es, x.answer_en, x.answer_es, x.tags].filter(Boolean).join(" "));
    return fqWords.every((w) => hay.includes(w));
  });

  const openNewVideo = (category) => setVideoDlg({ ...EMPTY_VIDEO, category });

  return (
    <div className="p-8 max-w-6xl" data-testid="training-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-md bg-[#2495D3]/10 flex items-center justify-center">
            <GraduationCap className="text-[#2495D3]" size={22} />
          </div>
          <div>
            <h1 className="font-head font-black text-2xl tracking-tight">{t("Training Center", "Centro de Entrenamiento")}</h1>
            <p className="text-sm text-slate-500">{t("Learn the software, products and machines.", "Aprende el software, los productos y las máquinas.")}</p>
          </div>
        </div>
        <Link to="/print-studio-manual" data-testid="training-studio-manual-link">
          <Button variant="outline" size="sm" className="border-[#2495D3]/40 text-[#2495D3] hover:bg-[#2495D3]/5">
            <Wand2 size={15} className="mr-1.5" /> {t("Print Studio Manual", "Manual de Print Studio")}
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="faq">
        <TabsList data-testid="training-tabs">
          <TabsTrigger value="faq" data-testid="tab-faq">{t("FAQ / Search", "Preguntas / Buscar")}</TabsTrigger>
          <TabsTrigger value="manual" data-testid="tab-manual">{t("System Manual", "Manual del Sistema")}</TabsTrigger>
          <TabsTrigger value="tours" data-testid="tab-tours">{t("Guided Tours", "Recorridos")}</TabsTrigger>
          <TabsTrigger value="product" data-testid="tab-product">{t("Products", "Productos")}</TabsTrigger>
          <TabsTrigger value="machine" data-testid="tab-machine">{t("Machines", "Máquinas")}</TabsTrigger>
          <TabsTrigger value="general" data-testid="tab-general">{t("General Library", "Biblioteca General")}</TabsTrigger>
        </TabsList>

        {/* FAQ / SEARCH */}
        <TabsContent value="faq" className="mt-6">
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <div className="relative flex-1 min-w-[240px] max-w-xl">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="faq-search"
                value={faqQuery}
                onChange={(e) => setFaqQuery(e.target.value)}
                placeholder={t("Search… e.g. how is booklet pricing calculated", "Buscar… ej. cómo se calcula el precio del booklet")}
                className="pl-9"
              />
            </div>
            <select data-testid="faq-module-filter" value={faqModule} onChange={(e) => setFaqModule(e.target.value)} className="text-sm border border-slate-200 rounded-md h-10 px-2 bg-white">
              <option value="all">{t("All sections", "Todas las secciones")}</option>
              <option value="dashboard">Dashboard</option>
              {FAQ_AREAS.map((a) => (
                <optgroup key={a.group} label={a.label_en}>
                  {a.modules.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </optgroup>
              ))}
              <option value="general">General</option>
            </select>
          </div>
          {isAdmin && (
            <div className="mb-4">
              <Button size="sm" variant="outline" data-testid="add-faq-btn" onClick={() => setFaqDlg({ ...EMPTY_FAQ })}>
                <Plus size={15} className="mr-1" /> {t("Add Q&A", "Agregar pregunta")}
              </Button>
            </div>
          )}
          <Accordion type="single" collapsible className="space-y-2">
            {filteredFaqs.map((x) => (
              <AccordionItem key={x.id} value={x.id} className="border border-slate-200 rounded-md px-4 bg-white" data-testid={`faq-item-${x.id}`}>
                <AccordionTrigger className="text-left font-semibold hover:no-underline">
                  <span className="flex items-center gap-2 flex-wrap">
                    {t(x.question_en, x.question_es)}
                    {x.module && x.module !== "general" && <span className="text-[10px] font-mono uppercase tracking-wide text-[#2495D3] bg-[#2495D3]/10 rounded px-1.5 py-0.5">{FAQ_MOD_LABEL[x.module] || x.module}</span>}
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="text-sm text-slate-700 leading-relaxed"><MD>{t(x.answer_en, x.answer_es)}</MD></div>
                  {isAdmin && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                      <Button size="sm" variant="ghost" onClick={() => setFaqDlg({ ...x })} data-testid={`edit-faq-${x.id}`}><Pencil size={14} className="mr-1" /> {t("Edit", "Editar")}</Button>
                      <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteFaq(x.id)}><Trash2 size={14} className="mr-1" /> {t("Delete", "Eliminar")}</Button>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {filteredFaqs.length === 0 && <p className="text-sm text-slate-500">{faqQuery ? t("No results for your search.", "Sin resultados para tu búsqueda.") : t("No questions yet.", "Aún no hay preguntas.")}</p>}
        </TabsContent>

        {/* MANUAL */}
        <TabsContent value="manual" className="mt-6">
          {isAdmin && (
            <div className="mb-4">
              <Button size="sm" variant="outline" data-testid="add-section-btn" onClick={() => setSectionDlg({ ...EMPTY_SECTION })}>
                <Plus size={15} className="mr-1" /> {t("Add section", "Agregar sección")}
              </Button>
            </div>
          )}
          {groups.map((g) => (
            <div key={g} className="mb-6">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2">{GROUP_LABEL[g] || g}</div>
              <Accordion type="single" collapsible className="space-y-2">
                {manual.filter((s) => s.group === g).map((s) => (
                  <AccordionItem key={s.id} value={s.id} className="border border-slate-200 rounded-md px-4 bg-white" data-testid={`manual-section-${s.id}`}>
                    <AccordionTrigger className="text-left font-semibold hover:no-underline">{t(s.title_en, s.title_es)}</AccordionTrigger>
                    <AccordionContent>
                      <div className="whitespace-pre-line text-sm text-slate-700 leading-relaxed">{t(s.body_en, s.body_es)}</div>
                      {isAdmin && (
                        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                          <Button size="sm" variant="ghost" onClick={() => setSectionDlg({ ...s })} data-testid={`edit-section-${s.id}`}><Pencil size={14} className="mr-1" /> {t("Edit", "Editar")}</Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteSection(s.id)}><Trash2 size={14} className="mr-1" /> {t("Delete", "Eliminar")}</Button>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
          {manual.length === 0 && <p className="text-sm text-slate-500">{t("No content yet.", "Aún no hay contenido.")}</p>}
        </TabsContent>

        {/* GUIDED TOURS */}
        <TabsContent value="tours" className="mt-6">
          <p className="text-sm text-slate-500 mb-4">{t("Interactive step-by-step tours that highlight the real buttons in the system with arrows.", "Recorridos interactivos paso a paso que resaltan los botones reales del sistema con flechas.")}</p>
          <div className="grid gap-4 md:grid-cols-2">
            {TOURS.filter((tr) => tr.roles.includes(user?.role)).map((tr) => (
              <div key={tr.id} className="border border-slate-200 rounded-md bg-white p-5 flex flex-col" data-testid={`tour-card-${tr.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Route size={16} className="text-[#2495D3]" />
                  <div className="font-semibold text-sm">{t(tr.title_en, tr.title_es)}</div>
                </div>
                <p className="text-sm text-slate-600 flex-1">{t(tr.desc_en, tr.desc_es)}</p>
                <Button size="sm" className="bg-[#2495D3] hover:bg-[#1E7AA9] mt-3 self-start" data-testid={`start-tour-${tr.id}`} onClick={() => startTour(tr.id)}>
                  <PlayCircle size={15} className="mr-1" /> {t("Start tour", "Iniciar recorrido")}
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* VIDEO TABS */}
        {["product", "machine", "general"].map((cat) => (
          <TabsContent key={cat} value={cat} className="mt-6">
            {isAdmin && (
              <div className="mb-4">
                <Button size="sm" data-testid={`add-video-${cat}`} className="bg-[#2495D3] hover:bg-[#1E7AA9]" onClick={() => openNewVideo(cat)}>
                  <Plus size={15} className="mr-1" /> {t("Add video", "Agregar video")}
                </Button>
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-2">
              {videos.filter((v) => v.category === cat).map((v) => (
                <div key={v.id} className="border border-slate-200 rounded-md bg-white overflow-hidden" data-testid={`video-card-${v.id}`}>
                  <div className="aspect-video bg-slate-900">
                    {v.embed_url ? (
                      <iframe src={v.embed_url} title={v.title_en || v.title_es} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                    ) : (
                      <a href={v.url} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center text-white/80 gap-2">
                        <PlayCircle size={40} /> <span className="text-xs">{t("Open link", "Abrir enlace")}</span>
                      </a>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-semibold text-sm">{t(v.title_en, v.title_es) || t("Untitled", "Sin título")}</div>
                    {v.ref_label && <div className="text-[11px] text-[#2495D3] font-mono mt-0.5">{v.ref_label}</div>}
                    {t(v.description_en, v.description_es) && <div className="text-sm text-slate-600 mt-1 whitespace-pre-line">{t(v.description_en, v.description_es)}</div>}
                    <div className="flex items-center gap-2 mt-3">
                      <a href={v.url} target="_blank" rel="noreferrer" className="text-xs text-slate-500 hover:text-[#2495D3] flex items-center gap-1"><ExternalLink size={13} /> {t("Source", "Fuente")}</a>
                      {isAdmin && (
                        <div className="ml-auto flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setVideoDlg({ ...v })} data-testid={`edit-video-${v.id}`}><Pencil size={14} /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteVideo(v.id)}><Trash2 size={14} /></Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {videos.filter((v) => v.category === cat).length === 0 && (
              <p className="text-sm text-slate-500">{t("No videos yet.", "Aún no hay videos.")}</p>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* VIDEO DIALOG */}
      <Dialog open={!!videoDlg} onOpenChange={(o) => !o && setVideoDlg(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="video-dialog">
          <DialogHeader><DialogTitle>{videoDlg?.id ? t("Edit video", "Editar video") : t("Add video", "Agregar video")}</DialogTitle></DialogHeader>
          {videoDlg && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t("Video link (YouTube, Vimeo, Drive, OneDrive)", "Enlace del video (YouTube, Vimeo, Drive, OneDrive)")}</Label>
                <Input data-testid="video-url" value={videoDlg.url} onChange={(e) => setVideoDlg({ ...videoDlg, url: e.target.value })} placeholder="https://youtu.be/..." className="mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Title (EN)</Label><Input data-testid="video-title-en" value={videoDlg.title_en} onChange={(e) => setVideoDlg({ ...videoDlg, title_en: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Título (ES)</Label><Input data-testid="video-title-es" value={videoDlg.title_es} onChange={(e) => setVideoDlg({ ...videoDlg, title_es: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Description (EN)</Label><Textarea rows={3} value={videoDlg.description_en} onChange={(e) => setVideoDlg({ ...videoDlg, description_en: e.target.value })} className="mt-1" /></div>
                <div><Label className="text-xs">Descripción (ES)</Label><Textarea rows={3} value={videoDlg.description_es} onChange={(e) => setVideoDlg({ ...videoDlg, description_es: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("Category", "Categoría")}</Label>
                  <Select value={videoDlg.category} onValueChange={(val) => setVideoDlg({ ...videoDlg, category: val, ref_id: "", ref_label: "" })}>
                    <SelectTrigger className="mt-1" data-testid="video-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">{t("Product", "Producto")}</SelectItem>
                      <SelectItem value="machine">{t("Machine", "Máquina")}</SelectItem>
                      <SelectItem value="general">{t("General", "General")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(videoDlg.category === "product" || videoDlg.category === "machine") && (
                  <div>
                    <Label className="text-xs">{videoDlg.category === "product" ? t("Linked product", "Producto ligado") : t("Linked machine", "Máquina ligada")}</Label>
                    <Select value={videoDlg.ref_id || ""} onValueChange={(val) => {
                      const item = refs[videoDlg.category].find((x) => x.id === val);
                      setVideoDlg({ ...videoDlg, ref_id: val, ref_label: item?.name || "" });
                    }}>
                      <SelectTrigger className="mt-1" data-testid="video-ref"><SelectValue placeholder={t("Select…", "Seleccionar…")} /></SelectTrigger>
                      <SelectContent>
                        {refs[videoDlg.category].map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setVideoDlg(null)}>{t("Cancel", "Cancelar")}</Button>
            <Button className="bg-[#2495D3] hover:bg-[#1E7AA9]" onClick={saveVideo} data-testid="save-video-btn">{t("Save", "Guardar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SECTION DIALOG */}
      <Dialog open={!!sectionDlg} onOpenChange={(o) => !o && setSectionDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="section-dialog">
          <DialogHeader><DialogTitle>{sectionDlg?.id ? t("Edit section", "Editar sección") : t("Add section", "Agregar sección")}</DialogTitle></DialogHeader>
          {sectionDlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Title (EN)</Label><Input value={sectionDlg.title_en} onChange={(e) => setSectionDlg({ ...sectionDlg, title_en: e.target.value })} className="mt-1" data-testid="section-title-en" /></div>
                <div><Label className="text-xs">Título (ES)</Label><Input value={sectionDlg.title_es} onChange={(e) => setSectionDlg({ ...sectionDlg, title_es: e.target.value })} className="mt-1" data-testid="section-title-es" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("Group", "Grupo")}</Label>
                  <Select value={sectionDlg.group} onValueChange={(val) => setSectionDlg({ ...sectionDlg, group: val })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="getting_started">{t("Getting Started", "Primeros Pasos")}</SelectItem>
                      <SelectItem value="estimating">{t("Estimating", "Cotización")}</SelectItem>
                      <SelectItem value="business">{t("Business", "Negocio")}</SelectItem>
                      <SelectItem value="admin">{t("Administration", "Administración")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">{t("Order", "Orden")}</Label><Input type="number" value={sectionDlg.order} onChange={(e) => setSectionDlg({ ...sectionDlg, order: Number(e.target.value) })} className="mt-1" /></div>
              </div>
              <div><Label className="text-xs">Body (EN)</Label><Textarea rows={6} value={sectionDlg.body_en} onChange={(e) => setSectionDlg({ ...sectionDlg, body_en: e.target.value })} className="mt-1" data-testid="section-body-en" /></div>
              <div><Label className="text-xs">Contenido (ES)</Label><Textarea rows={6} value={sectionDlg.body_es} onChange={(e) => setSectionDlg({ ...sectionDlg, body_es: e.target.value })} className="mt-1" data-testid="section-body-es" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSectionDlg(null)}>{t("Cancel", "Cancelar")}</Button>
            <Button className="bg-[#2495D3] hover:bg-[#1E7AA9]" onClick={saveSection} data-testid="save-section-btn">{t("Save", "Guardar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* FAQ DIALOG */}
      <Dialog open={!!faqDlg} onOpenChange={(o) => !o && setFaqDlg(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogHeader><DialogTitle>{faqDlg?.id ? t("Edit Q&A", "Editar pregunta") : t("Add Q&A", "Agregar pregunta")}</DialogTitle></DialogHeader>
          {faqDlg && (
            <div className="space-y-3">
              <div className="rounded-md border border-dashed border-[#2495D3]/40 bg-[#2495D3]/5 p-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs text-slate-600 flex items-center gap-1.5">
                    <ImagePlus size={15} className="text-[#2495D3]" />
                    {t("Upload 1+ screenshots — AI fills the form", "Sube 1+ capturas — la IA llena el formulario")}
                  </div>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/png,image/jpeg,image/webp" multiple className="hidden"
                      data-testid="faq-image-input"
                      onChange={(e) => { handleFaqImages(e.target.files); e.target.value = ""; }} />
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold rounded-md bg-[#2495D3] text-white px-3 py-1.5 hover:bg-[#1E7AA9]">
                      {aiBusy === "images" ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
                      {aiBusy === "images" ? t("Reading…", "Leyendo…") : t("Fill from photo(s)", "Llenar desde foto(s)")}
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button type="button" size="sm" variant="outline" data-testid="faq-translate-btn" disabled={!!aiBusy} onClick={autoTranslate}>
                  {aiBusy === "translate" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Languages size={14} className="mr-1" />}
                  {t("Auto-translate", "Auto-traducir")}
                </Button>
                <span className="text-[11px] text-slate-400">{t("Fill one language, then translate to the other", "Llena un idioma y traduce al otro")}</span>
              </div>

              {faqDlg.seed_key && (
                <div className="flex items-center justify-between gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 flex-wrap">
                  <span className="text-[11px] text-amber-700">{t("System question — auto-updates on republish when the pricing formula changes.", "Pregunta del sistema — se actualiza sola al republicar cuando cambia la fórmula de precios.")}</span>
                  {faqDlg.id && (
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid="faq-sync-btn" disabled={!!aiBusy} onClick={syncFaqDefault}>
                      {aiBusy === "sync" ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}
                      {t("Reset to system version", "Restablecer a versión del sistema")}
                    </Button>
                  )}
                </div>
              )}

              <div>
                <Label className="text-xs">{t("Section / module", "Sección / módulo")}</Label>
                <select data-testid="faq-module-select" value={faqDlg.module || "general"} onChange={(e) => setFaqDlg({ ...faqDlg, module: e.target.value })} className="mt-1 w-full text-sm border border-slate-200 rounded-md h-9 px-2 bg-white">
                  <option value="general">General</option>
                  <option value="dashboard">Dashboard</option>
                  {FAQ_AREAS.map((a) => (
                    <optgroup key={a.group} label={a.label_en}>
                      {a.modules.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Question (EN)</Label><Input value={faqDlg.question_en} onChange={(e) => setFaqDlg({ ...faqDlg, question_en: e.target.value })} className="mt-1" data-testid="faq-question-en" /></div>
                <div><Label className="text-xs">Pregunta (ES)</Label><Input value={faqDlg.question_es} onChange={(e) => setFaqDlg({ ...faqDlg, question_es: e.target.value })} className="mt-1" data-testid="faq-question-es" /></div>
              </div>
              <div><Label className="text-xs">Answer (EN) — {t("Markdown & tables supported", "Soporta Markdown y tablas")}</Label><Textarea rows={7} value={faqDlg.answer_en} onChange={(e) => setFaqDlg({ ...faqDlg, answer_en: e.target.value })} className="mt-1 font-mono text-xs" data-testid="faq-answer-en" /></div>
              <div><Label className="text-xs">Respuesta (ES) — {t("Markdown & tables supported", "Soporta Markdown y tablas")}</Label><Textarea rows={7} value={faqDlg.answer_es} onChange={(e) => setFaqDlg({ ...faqDlg, answer_es: e.target.value })} className="mt-1 font-mono text-xs" data-testid="faq-answer-es" /></div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">{t("Keywords / tags (help search find it)", "Palabras clave (para que la búsqueda lo encuentre)")}</Label>
                  <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-[#2495D3]" data-testid="faq-keywords-btn" disabled={!!aiBusy} onClick={suggestKeywords}>
                    {aiBusy === "kw" ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Sparkles size={13} className="mr-1" />}
                    {t("Suggest", "Sugerir")}
                  </Button>
                </div>
                <Input value={faqDlg.tags} onChange={(e) => setFaqDlg({ ...faqDlg, tags: e.target.value })} className="mt-1" placeholder="booklet, precio, binding, grapa" data-testid="faq-tags" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFaqDlg(null)}>{t("Cancel", "Cancelar")}</Button>
            <Button className="bg-[#2495D3] hover:bg-[#1E7AA9]" onClick={saveFaq} data-testid="save-faq-btn">{t("Save", "Guardar")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
